import { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import { eventBus } from "../utils/eventBus";

import {
  AI_PENDING,
  ALL_PROVIDERS,
  FAX_DELETED_DAYS,
  FAX_DELETED_UNRECORDED,
  FAX_DONE_PICKS,
  FAX_NOTE_MAX,
  FAX_STATUS_FILTERS,
  PROVIDER_SOURCE_TEXT,
  faxActiveFilters,
  faxAiLabelsOn,
  faxProviderChips,
  faxProviderKey,
  faxProviderName,
  faxUserChips,
  faxUserName,
  faxWorkActionText,
  faxIsDeleted,
  faxJunkHint,
  faxWorkLine,
  faxWorkOf,
} from "./faxWork";
import {
  faxSetAssignee,
  faxSetPatient,
  faxSetProvider,
  faxWorkAct,
  faxWorkDetail,
  faxWorkErrorMessage,
  restoreFax,
} from "./faxApi";

/*
 * Fax work UI (owner 10-11; contract in faxWork.js):
 *   FaxWorkFilters   To work / Done / Review / Deleted / All + provider chips
 *                    with counts (Deleted: the received-in-the-last-days pick)
 *   FaxJunkLine      the AI's "Likely spam" hint + one-click Delete as spam
 *   FaxWorkListLine  the row's provider, a Done mark and the latest note
 *   FaxWorkPanel     in the viewer: provider (staff can change it), confirm
 *                    the suggested patient, Mark done / Reopen / Note /
 *                    Review, and the whole history ("Irma can know what
 *                    happened on that fax")
 */

// Calm chips (owner 10-13: one accent, no filled saturated pills): the
// selected one is a navy tint with a navy border, the others plain.
const pill = (on) =>
  `whitespace-nowrap rounded-md border px-2 py-[1px] text-[12px] leading-5 transition ${
    on
      ? "border-[#1D2951]/40 bg-[#1D2951]/[0.07] font-semibold text-[#1D2951]"
      : "border-[#E4E7EC] bg-white text-[#344054] hover:bg-[#F8FAFC]"
  }`;
const seg = (on) =>
  `h-7 whitespace-nowrap px-2.5 text-[12px] transition ${
    on ? "bg-[#1D2951]/[0.08] font-semibold text-[#1D2951]" : "bg-white text-[#475467] hover:bg-[#F8FAFC]"
  }`;

/*
 * ONE row (owner 10-13, "everything moved up"): status as a segmented
 * control, "Provider | User" (when the server tracks assignees), then the
 * chips of that kind (scrolling sideways rather than wrapping onto more
 * rows), and `children` (the list's icon buttons) at the far right.
 */
export function FaxWorkFilters({
  rows,
  // v2 GET fax/all provider_counts in the All (30 days) view
  serverCounts = null,
  providers,
  filters,
  onChange,
  available,
  deletedCount = 0,
  deletedDays = 7,
  onDeletedDays,
  staff = [],
  me = null,
  assignAvailable = false,
  children = null,
}) {
  if (!available) {
    return children ? (
      <div className="flex items-center justify-end gap-2 px-2 pb-1.5" data-testid="fax-work-filters-off">
        {children}
      </div>
    ) : null;
  }
  const deletedView = filters.status === "deleted";
  // the Deleted view narrows by provider only (owner 10-22)
  const byUser = assignAvailable && filters.by === "user" && !deletedView;
  // counts = the selected tab's rows (owner 10-22)
  const chips = byUser
    ? faxUserChips(rows, staff, filters.user, me, filters.status)
    : faxProviderChips(rows, providers, filters.provider, undefined, filters.status, serverCounts);
  return (
    <div className="flex min-w-0 items-center gap-2 px-2 pb-1.5 text-start" data-testid="fax-work-filters">
      <div
        className="flex shrink-0 overflow-hidden rounded-md border border-[#D0D5DD] divide-x divide-[#E4E7EC]"
        role="group"
        aria-label="Fax status"
      >
        {FAX_STATUS_FILTERS.map((s) => (
          <button
            key={s.id}
            type="button"
            aria-pressed={filters.status === s.id}
            className={seg(filters.status === s.id)}
            onClick={() => onChange({ ...filters, status: s.id })}
            data-testid={`fax-status-${s.id}`}
          >
            {s.label}
          </button>
        ))}
      </div>
      {deletedView && (
        <div className="flex shrink-0 flex-wrap items-center gap-1 text-[12px] text-[#667085]" data-testid="fax-deleted-days">
          <span>
            {deletedCount} deleted · received in the last
          </span>
          {FAX_DELETED_DAYS.map((d) => (
            <button
              key={d}
              type="button"
              aria-pressed={Number(deletedDays) === d}
              className={pill(Number(deletedDays) === d)}
              onClick={() => typeof onDeletedDays === "function" && onDeletedDays(d)}
              data-testid={`fax-deleted-days-${d}`}
            >
              {d} days
            </button>
          ))}
        </div>
      )}
      <>
          {assignAvailable && !deletedView && (
            <div
              className="flex shrink-0 overflow-hidden rounded-md border border-[#D0D5DD] divide-x divide-[#E4E7EC]"
              role="group"
              aria-label="Group by"
            >
              <button
                type="button"
                aria-pressed={!byUser}
                className={seg(!byUser)}
                onClick={() => onChange({ ...filters, by: "provider" })}
                data-testid="fax-by-provider"
              >
                Provider
              </button>
              <button
                type="button"
                aria-pressed={byUser}
                className={seg(byUser)}
                onClick={() => onChange({ ...filters, by: "user" })}
                data-testid="fax-by-user"
              >
                User
              </button>
            </div>
          )}
          <div
            className="flex min-w-0 flex-1 gap-1 overflow-x-auto [scrollbar-width:thin]"
            role="group"
            aria-label={byUser ? "User" : "Provider"}
          >
            {chips.map((c) => {
              const on = byUser ? String(filters.user) === c.key : String(filters.provider) === c.key;
              return (
                <button
                  key={c.key}
                  type="button"
                  aria-pressed={on}
                  className={pill(on)}
                  onClick={() => onChange(byUser ? { ...filters, user: c.key } : { ...filters, provider: c.key })}
                  title={
                    c.key === ALL_PROVIDERS
                      ? byUser
                        ? "Every user"
                        : "Every provider"
                      : c.key === AI_PENDING
                        ? `${c.count} not read by the AI yet: the provider may still come from the fax`
                        : `${c.label}: ${c.count}`
                  }
                  data-testid={`${byUser ? "fax-user-chip" : "fax-provider-chip"}-${c.key}`}
                >
                  {c.label} <span className="font-normal text-[#667085]">{c.count}</span>
                </button>
              );
            })}
          </div>
      </>
      {children && <div className="ml-auto flex shrink-0 items-center gap-1">{children}</div>}
    </div>
  );
}

/*
 * The picks narrowing the list as removable chips (owner 10-22: a remembered
 * "Assigned: me" silently hid faxes in the Inbox): "Assigned to: me ×",
 * "Provider: Dr. Malik ×" and Clear all. Nothing when no pick narrows it.
 */
export function FaxActiveFilterChips({ filters, onChange, combine = false, assignOn = false, providers = [], staff = [], large = false }) {
  const active = faxActiveFilters(filters, { combine, assignOn, providers, staff });
  if (!active.length) return null;
  const size = large ? "text-[13px] px-2.5 py-1" : "text-[12px] px-2 py-[1px]";
  return (
    <div className="flex flex-wrap items-center gap-1.5 px-2 pb-1.5 text-start" data-testid="fax-active-filters" aria-label="Active filters">
      <span className={`${large ? "text-[13px]" : "text-[12px]"} text-[#667085]`}>Showing only:</span>
      {active.map((a) => (
        <span
          key={a.key}
          className={`inline-flex items-center gap-1 rounded-full border border-[#1D2951]/30 bg-[#1D2951]/[0.06] font-medium text-[#1D2951] ${size}`}
          data-testid={`fax-active-filter-${a.key}`}
        >
          {a.label}
          <button
            type="button"
            onClick={() => onChange(a.clear)}
            aria-label={`Remove filter ${a.label}`}
            title="Remove this filter"
            className="ml-0.5 leading-none text-[#475467] hover:text-[#B42318]"
            data-testid={`fax-active-filter-${a.key}-remove`}
          >
            ×
          </button>
        </span>
      ))}
      {active.length > 1 && (
        <button
          type="button"
          onClick={() => onChange({ ...filters, user: "all", provider: ALL_PROVIDERS })}
          className={`${large ? "text-[13px]" : "text-[12px]"} text-[#475467] underline hover:text-[#1D2951]`}
          data-testid="fax-active-filters-clear"
        >
          Clear all
        </button>
      )}
    </div>
  );
}

// "AI pending" (owner 10-22): no provider yet and the AI has not read the
// fax, so its provider may still come from the fax (not "No provider").
export const faxRowAiPending = (row) => faxAiLabelsOn() && faxProviderKey(row) === AI_PENDING;

// The AI's "likely spam / not for us" hint on a list row, with a one-click
// "Delete as spam" (logged with reason spam; restorable under Deleted).
export function FaxJunkLine({ row, active = false, onDeleteAsSpam }) {
  const hint = faxJunkHint(row);
  if (!hint) return null;
  return (
    <div className="flex max-w-full items-center gap-1.5" data-testid="fax-junk-line">
      <span
        className={`truncate rounded-full px-1.5 text-[10px] font-medium ${
          active ? "bg-white/20 text-white" : "bg-[#FFFAEB] text-[#B54708]"
        }`}
        title={`${hint.text} (from the AI read; check before deleting)`}
        data-testid="fax-junk-hint"
      >
        {hint.text}
      </span>
      {typeof onDeleteAsSpam === "function" && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDeleteAsSpam();
          }}
          className={`whitespace-nowrap text-[10px] underline ${active ? "text-white" : "text-[#B42318]"}`}
          title="Delete now, logged as spam (it can be restored under Deleted)"
          data-testid="fax-delete-spam"
        >
          Delete as spam
        </button>
      )}
    </div>
  );
}

export function FaxWorkListLine({ row, providers, staff = [], active = false }) {
  const w = faxWorkOf(row);
  const line = faxWorkLine(row);
  const deleted = faxIsDeleted(row);
  const prov = w.providerid !== null ? faxProviderName(providers, w.providerid) : "";
  const who = w.assignee_user_id !== null ? faxUserName(staff, w.assignee_user_id, w.assignee_name) : "";
  const aiPending = !prov && faxRowAiPending(row);
  if (!line && !prov && !who && !aiPending && w.status !== "done" && !w.needs_review && !deleted) return null;
  const grey = active ? "text-[#FFFFFFCC]" : "text-[#667085]";
  return (
    <div className="flex w-full min-w-0 flex-col items-start gap-0.5" data-testid="fax-work-line">
      <div className="flex max-w-full items-center gap-1">
        {deleted && (
          <span
            className={`rounded-full px-1.5 text-[10px] font-medium ${
              active ? "bg-white/20 text-white" : "bg-[#FEF3F2] text-[#B42318]"
            }`}
            data-testid="fax-work-deleted"
          >
            Deleted
          </span>
        )}
        {!deleted && w.status === "done" && (
          <span
            className={`rounded-full px-1.5 text-[10px] font-medium ${
              active ? "bg-white/20 text-white" : "bg-[#ECFDF3] text-[#027A48]"
            }`}
            data-testid="fax-work-done"
          >
            Done
          </span>
        )}
        {w.needs_review && (
          <span
            className={`rounded-full px-1.5 text-[10px] font-medium ${
              active ? "bg-white/20 text-white" : "bg-[#FFFAEB] text-[#B54708]"
            }`}
            data-testid="fax-work-review"
          >
            Review
          </span>
        )}
        {prov && (
          <span className={`truncate text-[10px] ${grey}`} title={`For ${prov}`} data-testid="fax-work-provider">
            For {prov}
          </span>
        )}
        {aiPending && (
          <span
            className={`truncate text-[10px] italic ${grey}`}
            title="The AI has not read this fax yet: its provider may still come from the fax"
            data-testid="fax-work-ai-pending"
          >
            AI pending
          </span>
        )}
        {who && (
          <span
            className={`truncate text-[10px] font-medium ${active ? "text-white" : "text-[#1D2951]"}`}
            title={`Assigned to ${who}`}
            data-testid="fax-work-assignee"
          >
            → {who}
          </span>
        )}
      </div>
      {line && (
        <div className={`w-full truncate text-start text-[10px] ${grey}`} title={line} data-testid="fax-work-note">
          {line}
        </div>
      )}
    </div>
  );
}

const DIALOG_TITLE = {
  done: "Mark done",
  reopen: "Reopen fax",
  note: "Add a note",
  review: "Flag for review",
  restore: "Restore fax",
};

// done: a quick pick or a note; reopen / note: a note; review: optional note.
export function canSubmitWork(mode, pick, note) {
  const n = (note || "").trim();
  if (n.length > FAX_NOTE_MAX) return false;
  if (mode === "done") return Boolean(pick || n);
  if (mode === "reopen" || mode === "note") return Boolean(n);
  return true; // review, restore: the note is optional
}

export function FaxWorkDialog({ mode, initialPick = "", saving = false, error = "", onSubmit, onCancel }) {
  const [pick, setPick] = useState(initialPick || "");
  const [note, setNote] = useState("");
  const ok = canSubmitWork(mode, pick, note) && !saving;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="fax-work-dialog">
      <div className="fixed inset-0 bg-black/40" onClick={onCancel} />
      <div className="relative mx-4 w-full max-w-md rounded-lg bg-white p-5 text-start shadow-xl">
        <h2 className="mb-3 text-lg font-semibold text-gray-900">{DIALOG_TITLE[mode] || "Fax"}</h2>
        {mode === "done" && (
          <div className="mb-3">
            <div className="mb-1 text-xs font-medium text-[#475467]">What happened?</div>
            <div className="flex flex-wrap gap-1.5">
              {FAX_DONE_PICKS.map((p) => (
                <button
                  key={p}
                  type="button"
                  aria-pressed={pick === p}
                  className={pill(pick === p)}
                  onClick={() => setPick(pick === p ? "" : p)}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
        )}
        <label className="text-xs font-medium text-[#475467]" htmlFor="fax-work-note">
          {mode === "done"
            ? "Note (optional with a pick)"
            : mode === "review" || mode === "restore"
              ? "Note (optional)"
              : "Note"}
        </label>
        <textarea
          id="fax-work-note"
          rows={3}
          maxLength={FAX_NOTE_MAX}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={
            mode === "reopen"
              ? "Why is it reopened?"
              : mode === "restore"
                ? "Why is it back? (e.g. not spam after all)"
                : "What was done, for whoever looks next"
          }
          className="mt-1 w-full resize-none rounded-md border px-3 py-2 text-sm"
        />
        <div className="mt-0.5 text-right text-[10px] text-[#98A2B3]">
          {note.length}/{FAX_NOTE_MAX}
        </div>
        {error && (
          <div className="mt-1 text-xs text-[#B42318]" role="alert">
            {error}
          </div>
        )}
        <div className="mt-3 flex justify-end gap-2">
          <button type="button" onClick={onCancel} className="rounded-md bg-gray-100 px-4 py-2 text-sm text-gray-700">
            Cancel
          </button>
          <button
            type="button"
            disabled={!ok}
            onClick={() => onSubmit({ quickPick: mode === "done" ? pick : "", note: note.trim() })}
            className={`rounded-md px-4 py-2 text-sm text-white ${ok ? "bg-[#1D2951]" : "cursor-not-allowed bg-gray-300"}`}
            data-testid="fax-work-save"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

const when = (ts) => (ts ? dayjs(ts).format("MM/DD h:mm A") : "");

// "Deleted by Irma T 10/12 9:05 AM · Spam — toner ad"
export const deletedByText = (by, at, pick, note) =>
  `Deleted by ${by || "System"}${at ? ` ${when(at)}` : ""}${pick ? ` · ${pick}` : ""}${note ? ` — ${note}` : ""}`;

function historyText(e, providers, staff) {
  if (e.action === "assignee" && e.detail) {
    const to = e.detail.to === null || e.detail.to === undefined ? "" : faxUserName(staff, e.detail.to);
    const how = e.detail.source === "fax" ? " (named on the fax)" : "";
    return to ? `Assigned to ${to}${how}` : "Unassigned";
  }
  if (e.action === "provider" && e.detail) {
    const to = e.detail.to === null || e.detail.to === undefined ? "Unassigned" : faxProviderName(providers, e.detail.to);
    return `Provider: ${to}`;
  }
  if (e.action === "patient" && e.detail) {
    const how = e.detail.how === "filed" ? "filed" : e.detail.how === "confirmed" ? "confirmed" : "picked";
    const to = e.detail.to !== undefined && e.detail.to !== null ? ` · for ${faxProviderName(providers, e.detail.to)}` : "";
    return `Patient ${how}${to}`;
  }
  return [faxWorkActionText(e.action), e.quick_pick].filter(Boolean).join(" · ");
}

/*
 * The viewer's work block. `request` ({mode, pick, nonce}) lets the table open
 * the dialog itself (after a filing: Mark done with "Filed to chart" picked).
 */
export function FaxWorkPanel({
  row,
  providers,
  available,
  suggestedPatientId = null,
  request = null,
  onSaved,
  staff = [],
  assignAvailable = false,
  canAssign = false,
}) {
  const faxKey = row ? String(row.faxid ?? row.id ?? "") : "";
  const [history, setHistory] = useState([]);
  const [work, setWork] = useState(() => faxWorkOf(row));
  const [dialog, setDialog] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [showAll, setShowAll] = useState(false);
  const loadRef = useRef(0);

  // the list's copy when it changes (a re-read), the server's on open
  const rowWorkKey = JSON.stringify(row?.work || null);
  useEffect(() => {
    setWork(faxWorkOf(row));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [faxKey, rowWorkKey]);

  useEffect(() => {
    if (!available || !faxKey) return undefined;
    const load = ++loadRef.current;
    setHistory([]);
    setError("");
    setShowAll(false);
    faxWorkDetail(row)
      .then((body) => {
        if (load !== loadRef.current || !body) return;
        setHistory(Array.isArray(body.history) ? body.history : []);
        if (body.work) setWork(faxWorkOf({ work: body.work }));
      })
      .catch(() => {});
    return () => {
      loadRef.current += 1;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [faxKey, available]);

  useEffect(() => {
    if (request && request.mode) setDialog({ mode: request.mode, pick: request.pick || "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request?.nonce]);

  if (!available || !row) return null;

  const applied = (body) => {
    if (body?.work) setWork(faxWorkOf({ work: body.work }));
    if (Array.isArray(body?.history)) setHistory(body.history);
    if (typeof onSaved === "function") onSaved(body?.work || null);
  };

  const run = async (fn) => {
    setSaving(true);
    setError("");
    try {
      applied(await fn());
      return true;
    } catch (e) {
      setError(faxWorkErrorMessage(e));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const submitDialog = async ({ quickPick, note }) => {
    const ok = await run(() =>
      dialog.mode === "restore" ? restoreFax(row, note) : faxWorkAct(row, { action: dialog.mode, note, quickPick })
    );
    if (ok) setDialog(null);
  };

  const changeProvider = (value) => run(() => faxSetProvider(row, value === "" ? null : value));
  const changeAssignee = (value) =>
    run(() => faxSetAssignee(row, value === "" ? null : value)).then((ok) => {
      // the side nav's "Assigned to me" count (fax/useFaxAssignedCount.js)
      if (ok) eventBus.emit("fax:assigned-changed");
    });
  const confirmPatient = () => run(() => faxSetPatient(row, suggestedPatientId, "confirmed"));

  const done = work.status === "done";
  // a fax of the Deleted view: who deleted it and why, Restore and notes only
  const deleted = faxIsDeleted(row);
  const deletedEntry = deleted ? history.find((e) => e.action === "deleted") || null : null;
  const showConfirm = suggestedPatientId !== null && suggestedPatientId !== undefined && work.patient_source !== "staff";
  const hint = work.providerid !== null ? PROVIDER_SOURCE_TEXT[work.provider_source] || "" : "";
  // 10-13 (compact viewer): the newest entry only, the rest one click away
  const shown = showAll ? history : history.slice(0, 1);
  // a login that is no longer on the staff list stays visible as the current pick
  const assigneeId = work.assignee_user_id === null ? "" : String(work.assignee_user_id);
  const staffOptions =
    assigneeId && !(staff || []).some((u) => String(u.user_id) === assigneeId)
      ? [...(staff || []), { user_id: work.assignee_user_id, name: work.assignee_name || `User ${assigneeId}` }]
      : staff || [];
  const sel = "h-7 max-w-[11rem] rounded-md border border-[#D0D5DD] bg-white px-1.5 text-xs text-[#1D2951]";

  return (
    <div className="mx-3 mt-1 rounded-lg border border-[#EAECF0] px-2.5 py-1.5 text-start" data-testid="fax-work-panel">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
        <label className="font-medium text-[#344054]" htmlFor={`fax-provider-${faxKey}`}>
          For
        </label>
        <select
          id={`fax-provider-${faxKey}`}
          value={work.providerid === null ? "" : String(work.providerid)}
          onChange={(e) => changeProvider(e.target.value)}
          disabled={saving || deleted}
          title={hint ? `Provider (${hint})` : "Provider"}
          className={sel}
          data-testid="fax-work-provider-select"
        >
          <option value="">Unassigned</option>
          {(providers || []).map((p) => (
            <option key={p.providerid} value={String(p.providerid)}>
              {p.name}
            </option>
          ))}
        </select>
        {hint && <span className="hidden text-[#98A2B3] xl:inline">({hint})</span>}
        {assignAvailable && (
          <>
            <label className="font-medium text-[#344054]" htmlFor={`fax-assignee-${faxKey}`}>
              Assign to
            </label>
            <select
              id={`fax-assignee-${faxKey}`}
              value={assigneeId}
              onChange={(e) => changeAssignee(e.target.value)}
              disabled={saving || deleted || !canAssign}
              title={
                !canAssign
                  ? "Assigning faxes needs the \"Fax Assign\" permission"
                  : work.assignee_source === "fax"
                    ? "Assigned from the fax (it names this person)"
                    : "Staff user working this fax"
              }
              className={sel}
              data-testid="fax-work-assignee-select"
            >
              <option value="">Nobody</option>
              {staffOptions.map((u) => (
                <option key={u.user_id} value={String(u.user_id)}>
                  {u.name}
                </option>
              ))}
            </select>
          </>
        )}
        {showConfirm && !deleted && (
          <button
            type="button"
            onClick={confirmPatient}
            disabled={saving}
            className="h-7 rounded-md border border-[#50869A] px-2 text-[#50869A]"
            title="The suggested patient is right: match the fax to that chart"
            data-testid="fax-work-confirm-patient"
          >
            Confirm suggested patient
          </button>
        )}
        <span className="ml-auto flex flex-wrap gap-1">
          {deleted ? (
            <button
              type="button"
              className={pill(true)}
              onClick={() => setDialog({ mode: "restore" })}
              data-testid="fax-work-restore"
            >
              Restore
            </button>
          ) : done ? (
            <button type="button" className={pill(false)} onClick={() => setDialog({ mode: "reopen" })}>
              Reopen
            </button>
          ) : (
            <button
              type="button"
              className={pill(true)}
              onClick={() => setDialog({ mode: "done" })}
              data-testid="fax-work-mark-done"
            >
              Mark done
            </button>
          )}
          <button type="button" className={pill(false)} onClick={() => setDialog({ mode: "note" })}>
            Add note
          </button>
          {deleted ? null : work.needs_review ? (
            <button
              type="button"
              className={pill(false)}
              onClick={() => run(() => faxWorkAct(row, { action: "unreview" }))}
            >
              Clear review
            </button>
          ) : (
            <button type="button" className={pill(false)} onClick={() => setDialog({ mode: "review" })}>
              Needs review
            </button>
          )}
        </span>
      </div>
      {deleted && (
        <div className="mt-1 text-xs text-[#B42318]" data-testid="fax-work-deleted-by">
          {deletedEntry
            ? deletedByText(deletedEntry.user_name, deletedEntry.created_at, deletedEntry.quick_pick, deletedEntry.note)
            : work.last_action === "deleted"
              ? deletedByText(work.last_by, work.last_at, work.last_quick_pick, work.last_note)
              : history.length === 0 && !work.last_action
                ? FAX_DELETED_UNRECORDED
                : "Deleted"}
        </div>
      )}
      {error && !dialog && (
        <div className="mt-1 text-xs text-[#B42318]" role="alert">
          {error}
        </div>
      )}
      {history.length > 0 && (
        <ul className="mt-1 space-y-0.5 text-[11px]" data-testid="fax-work-history">
          {shown.map((e) => (
            <li key={e.id} className="truncate text-[#475467]" title={e.note || undefined}>
              <span className="text-[#98A2B3]">{when(e.created_at)}</span>{" "}
              <span className="font-medium">{e.user_name || "System"}</span> · {historyText(e, providers, staff)}
              {e.note ? <span className="text-[#344054]"> — {e.note}</span> : null}
              {!showAll && history.length > 1 && e === shown[shown.length - 1] && (
                <button
                  type="button"
                  className="ml-2 text-[#2B7FFF] hover:underline"
                  onClick={() => setShowAll(true)}
                >
                  History ({history.length})
                </button>
              )}
            </li>
          ))}
          {showAll && history.length > 1 && (
            <li>
              <button type="button" className="text-[#2B7FFF] hover:underline" onClick={() => setShowAll(false)}>
                Show less
              </button>
            </li>
          )}
        </ul>
      )}
      {dialog && (
        <FaxWorkDialog
          key={`${faxKey}:${dialog.mode}:${dialog.pick || ""}`}
          mode={dialog.mode}
          initialPick={dialog.pick}
          saving={saving}
          error={error}
          onSubmit={submitDialog}
          onCancel={() => {
            setDialog(null);
            setError("");
          }}
        />
      )}
    </div>
  );
}
